import type { FieldNamesMarkedBoolean } from "react-hook-form";
import { z } from "zod";

import type { PersonPatchRequest, User } from "@/api/people";
import { emailSchema } from "@/lib/emailValidation";

export type OfficeRole = "admin" | "viewer" | "office_staff";
export type RoleGroup = "office" | "worker";

export const OFFICE_ROLES: OfficeRole[] = ["admin", "viewer", "office_staff"];
const personNameFields = {
  last_name: z.string().trim().min(1).max(200),
  first_name: z.string().trim().min(1).max(200),
  patronymic: z.string().trim().min(1).max(200),
};
const employmentDateFields = {
  hire_date: z.string().nullable(),
  termination_date: z.string().nullable(),
};
const employmentDatesAreOrdered = (value: {
  hire_date: string | null;
  termination_date: string | null;
}): boolean =>
  value.hire_date === null ||
  value.termination_date === null ||
  value.termination_date >= value.hire_date;
const employmentDateOrderError = {
  path: ["termination_date"],
  message: "employmentDateOrder",
};

export const officeSchema = z
  .object({
    group: z.literal("office"),
    ...personNameFields,
    role: z.enum(["admin", "viewer", "office_staff"]),
    unit_id: z.number().int().positive().nullable(),
    calendar_id: z.number().int().positive().nullable(),
    web_email: emailSchema,
    tab_number: z
      .string()
      .regex(/^[0-9]{3,6}$/, "tabFormat")
      .or(z.literal("")),
    position_id: z.number().int().positive().nullable(),
    tracks_time: z.boolean(),
    is_project_manager: z.boolean(),
    ...employmentDateFields,
  })
  .refine((value) => value.unit_id !== null, {
    path: ["unit_id"],
    message: "unitRequired",
  })
  .refine(
    (value) => !value.is_project_manager || value.role === "admin" || value.role === "office_staff",
    { path: ["is_project_manager"], message: "projectManagerRole" },
  )
  .refine(employmentDatesAreOrdered, employmentDateOrderError);

export const workerSchema = z
  .object({
    group: z.literal("worker"),
    ...personNameFields,
    tab_number: z.string().regex(/^[0-9]{3,6}$/, "tabFormat"),
    pin: z.string().regex(/^\d{4}$/, "pinFormat"),
    unit_id: z.number().int().positive({ message: "required" }),
    calendar_id: z.number().int().positive().nullable(),
    position_id: z.number().int().positive().nullable(),
    ...employmentDateFields,
  })
  .refine(employmentDatesAreOrdered, employmentDateOrderError);

export const workerEditSchema = z
  .object({
    group: z.literal("worker"),
    ...personNameFields,
    tab_number: z.string().regex(/^[0-9]{3,6}$/, "tabFormat"),
    unit_id: z.number().int().positive({ message: "required" }),
    calendar_id: z.number().int().positive().nullable(),
    position_id: z.number().int().positive().nullable(),
    tracks_time: z.boolean(),
    ...employmentDateFields,
  })
  .refine(employmentDatesAreOrdered, employmentDateOrderError);

export const personCreateSchema = z.discriminatedUnion("group", [officeSchema, workerSchema]);
export const personEditSchema = z.discriminatedUnion("group", [officeSchema, workerEditSchema]);

export type PersonCreateFormValues = z.infer<typeof personCreateSchema>;
export type PersonEditFormValues = z.infer<typeof personEditSchema>;
export type PersonFormValues = PersonCreateFormValues | PersonEditFormValues;
export type OfficeValues = z.infer<typeof officeSchema>;
export type WorkerValues = z.infer<typeof workerSchema>;
export type WorkerEditValues = z.infer<typeof workerEditSchema>;

export interface PersonDefaultsOptions {
  mode: "create" | "edit";
  user?: User;
  allowedRoles?: string[];
  lockedUnitId?: number | null;
  initialUnitId?: number | null;
}

export function getPersonFormDefaults({
  mode,
  user,
  allowedRoles,
  lockedUnitId = null,
  initialUnitId = null,
}: PersonDefaultsOptions): PersonFormValues {
  const roleOptions = allowedRoles ?? ["admin", "viewer", "office_staff", "worker"];
  const createGroup: RoleGroup = OFFICE_ROLES.some((role) => roleOptions.includes(role))
    ? "office"
    : "worker";
  const officeDefaults: OfficeValues = {
    group: "office",
    last_name: user?.last_name ?? "",
    first_name: user?.first_name ?? "",
    patronymic: user?.patronymic ?? "",
    role: user?.role && user.role !== "worker" ? (user.role as OfficeRole) : "office_staff",
    unit_id: user?.unit_id ?? lockedUnitId ?? initialUnitId ?? null,
    calendar_id: user?.calendar_id ?? null,
    web_email: user?.web_email ?? "",
    tab_number: user?.tab_number ?? "",
    position_id: user?.position_id ?? null,
    tracks_time: user?.tracks_time ?? false,
    is_project_manager: user?.is_project_manager ?? false,
    hire_date: user?.hire_date ?? null,
    termination_date: user?.termination_date ?? null,
  };
  const workerDefaults: WorkerValues = {
    group: "worker",
    last_name: "",
    first_name: "",
    patronymic: "",
    tab_number: "",
    pin: "",
    unit_id: lockedUnitId ?? initialUnitId ?? 0,
    calendar_id: null,
    position_id: null,
    hire_date: null,
    termination_date: null,
  };
  const workerEditDefaults: WorkerEditValues = {
    group: "worker",
    last_name: user?.last_name ?? "",
    first_name: user?.first_name ?? "",
    patronymic: user?.patronymic ?? "",
    tab_number: user?.tab_number ?? "",
    unit_id: user?.unit_id ?? 0,
    calendar_id: user?.calendar_id ?? null,
    position_id: user?.position_id ?? null,
    tracks_time: user?.tracks_time ?? false,
    hire_date: user?.hire_date ?? null,
    termination_date: user?.termination_date ?? null,
  };

  if (mode === "create") return createGroup === "worker" ? workerDefaults : officeDefaults;
  return user?.role === "worker" ? workerEditDefaults : officeDefaults;
}

export function buildPersonPatch(
  values: PersonEditFormValues,
  dirty: Partial<Readonly<FieldNamesMarkedBoolean<PersonEditFormValues>>>,
): PersonPatchRequest {
  const body: PersonPatchRequest = {};
  const nameDirty = dirty.last_name || dirty.first_name || dirty.patronymic;
  if (nameDirty) {
    body.last_name = values.last_name.trim();
    body.first_name = values.first_name.trim();
    body.patronymic = values.patronymic.trim();
  }
  if (dirty.tab_number) body.tab_number = values.tab_number || null;
  if (dirty.unit_id) body.unit_id = values.unit_id;
  if (dirty.calendar_id) body.calendar_id = values.calendar_id;
  if (dirty.position_id) body.position_id = values.position_id;
  if (dirty.tracks_time) body.tracks_time = values.tracks_time;
  if (values.group === "office" && "is_project_manager" in dirty && dirty.is_project_manager) {
    body.is_project_manager = values.is_project_manager;
  }
  if (dirty.hire_date) body.hire_date = values.hire_date;
  if (dirty.termination_date) body.termination_date = values.termination_date;
  if (values.group === "office" && "role" in dirty && dirty.role) body.role = values.role;
  return body;
}
